import type { ReviewApprovalState, ReviewReportMetadata } from "./reviewReport";

export const REVIEW_OUTCOME_LABELS = {
  approved: "Approved",
  comment: "Comments",
  changes_requested: "Changes requested",
} as const;

export const REVIEW_OUTCOME_CLASSES = {
  approved: "text-ds-green",
  comment: "text-neutral-700 dark:text-neutral-300",
  changes_requested: "text-amber-600 dark:text-amber-400",
} as const;

export const REVIEW_DISPOSITION_LABELS = {
  fix: "Fix",
  wont_fix: "Won't fix",
  fast_follow: "Fast follow",
} as const;

const NOT_AUTO_APPROVED_PHRASES = {
  draft: "the PR is a draft",
  closed: "the PR is closed",
  reviewed_base_missing: "the reviewed base is unknown",
  findings_not_posted: "the findings were never posted",
  github_unavailable: "GitHub was unavailable",
  no_lead: "no [code]smith session is following this PR to judge the findings",
} as const;

export type ReviewApprovalTone =
  | "pending"
  | "attention"
  | "success"
  | "success-muted"
  | "danger"
  | "neutral";

export interface ReviewApprovalPill {
  label: string;
  tone: ReviewApprovalTone;
  /** One line saying why. */
  tip: string;
}

/** Status-pill dots per DESIGN.md › Status chips, including its review-card exception for green. */
export const REVIEW_APPROVAL_DOT_CLASSES = {
  pending: "bg-[var(--chart-9)]",
  attention: "bg-[var(--chart-3)]",
  success: "bg-[var(--chart-12)]",
  "success-muted": "bg-[var(--chart-12)] opacity-50",
  danger: "bg-[var(--chart-5)]",
  neutral: "bg-[var(--chart-grey)]",
} as const satisfies Record<ReviewApprovalTone, string>;

/**
 * Auto-approve applies to the report's PR author; reports mirrored before
 * `approval` existed apply once they carry a verdict. A recorded approval
 * decision keeps its pill after auto-approve is turned off.
 */
export function reviewAutoApproveApplies(
  report: Pick<ReviewReportMetadata, "approval" | "verdict">,
): boolean {
  const decision = report.verdict?.approval_decision;
  if (decision != null && decision !== "skipped") return true;
  return report.approval?.applies ?? report.verdict !== undefined;
}

function count(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? "" : "s"}`;
}

const SUPERSEDED_PILL: ReviewApprovalPill = {
  label: "Superseded",
  tone: "neutral",
  tip: "A newer review replaced this round",
};

const OUTCOME_APPROVAL_STATES: ReadonlySet<ReviewApprovalState> = new Set([
  "approved",
  "approved_but_risky",
  "p1_declined",
  "risk_not_assessed",
]);

/** Whether the approval pill settles the round, `Approved` or `Needs human review`, and so stands in for the outcome badge. */
export function reviewApprovalSettlesOutcome(
  report: Pick<ReviewReportMetadata, "verdict">,
): boolean {
  const state = report.verdict?.approval_state;
  return state != null && OUTCOME_APPROVAL_STATES.has(state);
}

/**
 * The one approval-state pill for a review round, e.g. `Fixing 2 findings`,
 * read from the verdict's `approval_state`; null when auto-approve does not
 * apply, the lead has not given a verdict, or there is nothing to say. A round
 * that is not its PR's latest never reads as still pending.
 */
export function reviewApprovalPill(
  report: Pick<
    ReviewReportMetadata,
    "approval" | "verdict" | "review_result" | "wake"
  >,
  { isLatest }: { isLatest: boolean },
): ReviewApprovalPill | null {
  if (!reviewAutoApproveApplies(report)) return null;
  const { verdict } = report;
  const state = verdict?.approval_state;
  if (!verdict || !state) return null;
  const toFix = verdict.findings.filter(
    (finding) => finding.disposition === "fix",
  );
  const declined = verdict.findings.length - toFix.length;
  const pending = (pill: ReviewApprovalPill) =>
    isLatest ? pill : SUPERSEDED_PILL;
  const approvedTip =
    declined > 0
      ? `Lead approved; ${count(declined, "finding")} declined`
      : "Lead approved with no open findings";

  switch (state) {
    case "approved":
      return { label: "Approved", tone: "success", tip: approvedTip };
    case "approving":
      return pending({
        label: "Approving",
        tone: "pending",
        tip: "The approval is being submitted to GitHub",
      });
    case "approved_but_risky": {
      const risk = report.approval?.risk ?? report.review_result.risk?.level;
      return {
        label: "Needs human review",
        tone: "attention",
        tip: `Lead approved the code; auto-approve needs low risk${
          risk ? `, not ${risk}` : ""
        }`,
      };
    }
    case "p1_declined": {
      const declinedP1s = verdict.findings.filter(
        (finding) => finding.severity === "P1" && finding.disposition !== "fix",
      ).length;
      return {
        label: "Needs human review",
        tone: "attention",
        tip: `Lead declined ${count(declinedP1s, "P1 finding")}; only a human approves past a P1`,
      };
    }
    case "risk_not_assessed":
      return {
        label: "Needs human review",
        tone: "attention",
        tip: "Lead approved the code; the review didn't assess risk",
      };
    case "awaiting_proof":
      return pending({
        label: "Waiting for Proof",
        tone: "pending",
        tip: "Lead approved; the approval waits for Proof to finish",
      });
    case "proof_failed":
      return {
        label: "Proof found issues",
        tone: "danger",
        tip: "Proof found issues on this commit",
      };
    case "changes_needed": {
      const labels = toFix.map((finding) => finding.label).join(", ");
      const asked = `${labels}${declined > 0 ? `; ${declined} declined` : ""}`;
      if (isLatest && toFix.length > 0) {
        if (report.wake) {
          return {
            label: `${count(toFix.length, "finding")} to fix`,
            tone: "attention",
            tip: `${
              report.wake.skipped_reason === "round_cap"
                ? "Fixes wait for more review rounds"
                : "Fixes wait for autofix"
            }: ${asked}`,
          };
        }
        return {
          label: `Fixing ${count(toFix.length, "finding")}`,
          tone: "attention",
          tip: `Lead asked for fixes: ${asked}`,
        };
      }
      const parts = [
        toFix.length > 0 ? `${toFix.length} to fix` : null,
        declined > 0 ? `${declined} declined` : null,
      ].filter((part) => part !== null);
      return {
        label: "Changes needed",
        tone: "neutral",
        tip: `Lead asked for fixes${
          parts.length > 0 ? `: ${parts.join(", ")}` : ""
        }`,
      };
    }
    case "would_approve":
      return {
        label: "Would approve (shadow)",
        tone: "success-muted",
        tip: approvedTip,
      };
    case "approval_refused":
      return {
        label: "Approval refused",
        tone: "neutral",
        tip: "GitHub refused the approval",
      };
    case "approval_unconfirmed":
      return {
        label: "Approval unconfirmed",
        tone: "neutral",
        tip: "GitHub didn't confirm the approval; check the PR",
      };
    case "withdrawn":
      return {
        label: "Approval withdrawn",
        tone: "neutral",
        tip: "The approval was withdrawn",
      };
    case "would_withdraw":
      return {
        label: "Would withdraw (shadow)",
        tone: "neutral",
        tip: "The approval would have been withdrawn",
      };
    case "covered_by_earlier_approval":
      return {
        label: "Covered by earlier approval",
        tone: "neutral",
        tip: "An earlier round already covers this head",
      };
    case "head_moved":
    case "base_changed":
    case "merge_base_changed":
      return {
        label: "Superseded by a push",
        tone: "neutral",
        tip: "New commits were pushed before approval",
      };
    case "draft":
    case "closed":
    case "reviewed_base_missing":
    case "findings_not_posted":
    case "github_unavailable":
    case "no_lead":
      return {
        label: "Not auto-approved",
        tone: "neutral",
        tip: `Not approved: ${NOT_AUTO_APPROVED_PHRASES[state]}`,
      };
    case "auto_approve_off":
    case "bot_author":
    case "fork":
    default:
      return null;
  }
}

// Place it between whitespace-nowrap segments, never inside one: the NBSP
// keeps the middot with the segment before it, and the plain space after it
// is the line's only wrap point.
export const REVIEW_DETAIL_SEPARATOR = "\u00a0· ";

/**
 * Status-pill dots per DESIGN.md: the chip stays neutral, the dot carries the
 * outcome. These are review outcomes, not run statuses: `STATUS_COLORS.success`
 * is blue and would read the same as the indigo comment dot.
 */
export const REVIEW_OUTCOME_DOT_CLASSES = {
  approved: "bg-[var(--chart-12)]",
  comment: "bg-[var(--chart-9)]",
  changes_requested: "bg-[var(--chart-3)]",
} as const;
